import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React from 'react';
import { View } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api';
import { useCheckValidExtension } from '@/api/reservations/use-check-valid-extension';
import type { CreateExtensionIntent } from '@/api/reservations/use-create-extension-intent';
import { rgbaToHex } from '@/core';
import { Button, Card, showErrorMessage, Text } from '@/ui';

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

export default function ExtensionPayment({
  reservation,
  extensionIntent,
  onPaymentInitiated,
  onPaymentSucceeded,
  onPaymentFailed,
}: {
  reservation: Reservation;
  extensionIntent: CreateExtensionIntent;
  onPaymentInitiated: () => void;
  onPaymentSucceeded: () => void;
  onPaymentFailed: () => void;
}) {
  const { styles, theme } = useStyles();

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: extensionIntent.data.stripeSecret,
        customerSessionClientSecret: extensionIntent.data.customerSessionSecret,
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <ExtensionStripePayment
        reservation={reservation}
        extensionIntent={extensionIntent}
        onPaymentInitiated={() => {
          onPaymentInitiated();
        }}
        onPaymentSucceeded={() => {
          onPaymentSucceeded();
        }}
        onPaymentFailed={() => {
          onPaymentFailed();
        }}
      />
    </Elements>
  );
}

function ExtensionStripePayment({
  reservation,
  extensionIntent,
  onPaymentInitiated,
  onPaymentSucceeded,
  onPaymentFailed,
}: {
  reservation: Reservation;
  extensionIntent: CreateExtensionIntent;
  onPaymentInitiated: () => void;
  onPaymentSucceeded: () => void;
  onPaymentFailed: () => void;
}) {
  const { styles, theme } = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >();
  const [checkExtensionError, setCheckExtensionError] = React.useState('');

  const {
    mutateAsync: checkValidExtension,
    isPending: isCheckingValidExtension,
  } = useCheckValidExtension();

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setCheckExtensionError('');

    try {
      await checkValidExtension({
        id: reservation.id,
        endTime: extensionIntent.data.endTime,
      });
    } catch (e: any) {
      setCheckExtensionError(e.message);
      return;
    }

    onPaymentInitiated();

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log('submit error', submitError);
      onPaymentFailed();
      showErrorMessage('Payment failed. Please try again.');
      return;
    }

    let errorDuringBooking = null;
    if (paymentMethodId !== '-1') {
      const { error } = await stripe.confirmCardPayment(
        extensionIntent.data.stripeSecret,
        {
          payment_method: paymentMethodId as string,
        }
      );

      errorDuringBooking = error;
    } else if (paymentMethodId === '-1') {
      console.log('elements', elements);
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      errorDuringBooking = error;
    }

    if (errorDuringBooking) {
      console.log('error', errorDuringBooking);
      onPaymentFailed();
      showErrorMessage('Payment failed. Please try again.');
      return;
    }

    onPaymentSucceeded();
  };

  return (
    <View style={{ flexGrow: 1, gap: 16 }}>
      <Card>
        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.card.default.borderColor,
          }}
        >
          <View style={{ padding: 16 }}>
            <Text type="title2">Selected Payment Method</Text>
          </View>
        </View>

        <View style={{ padding: 16 }}>
          <PaymentElement
            onChange={(e) => {
              setIsFormValid(e.complete);
              setPaymentMethodId(e.value?.payment_method?.id || '-1');
            }}
            options={{
              business: { name: 'Eon' },
              layout: {
                type: 'accordion',
                radios: true,
                spacedAccordionItems: false,
              },
            }}
          />
        </View>
      </Card>

      <Button
        text={`Extend for ${extensionIntent.data.numDaysAdded} days - $${
          extensionIntent.data.subtotal +
          extensionIntent.data.outstandingBalance
        }`}
        type="primary"
        onPress={() => {
          handlePayment();
        }}
        disabled={!isFormValid}
      />

      {checkExtensionError ? (
        <Text type="caption1" style={{ color: theme.colors.error }}>
          {checkExtensionError}. Please contact support.
        </Text>
      ) : null}
    </View>
  );
}
